import { useEffect } from "react";
import "@/App.css";
import "@/styles/theme-overrides.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import IndustryPage from "@/pages/IndustryPage";
import AuditPage from "@/pages/AuditPage";
import BookingPage from "@/pages/BookingPage";
import AIServicesPage from "@/pages/AIServicesPage";
import AIToolPage from "@/pages/AIToolPage";
import CareersPage from "@/pages/CareersPage";
import ArtistsPage from "@/pages/ArtistsPage";
import CreatorHubPage from "@/pages/CreatorHubPage";
import AdminPage from "@/pages/AdminPage";
import ModelsPage from "@/pages/ModelsPage";
import ServicePage from "@/pages/ServicePage";
import AdStudioPage from "@/pages/AdStudioPage";
import AcademyPage from "@/pages/AcademyPage";
import FashionPage from "@/pages/FashionPage";
import InvestorsPage from "@/pages/InvestorsPage";
import InvestorArticlePage from "@/pages/InvestorArticlePage";
import AgencyPartnersPage from "@/pages/AgencyPartnersPage";
import SmritiChat from "@/components/SmritiChat";
import ThemeEngine from "@/components/ThemeEngine";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PAGE_TITLES = {
  "/": "Prosperabuzz — We Shape How Businesses Are Perceived",
  "/ai-services": "AI Marketing Services — Prosperabuzz",
  "/creators": "Creators x Brands — Prosperabuzz",
  "/careers": "Careers — Prosperabuzz",
  "/booking": "Join the Waitlist — Prosperabuzz",
  "/audit": "Digital Audit — Prosperabuzz",
  "/for-artists": "Song Promotion & Artist Marketing — Prosperabuzz",
  "/talent": "Model & Talent Agency — Prosperabuzz",
  "/ad-studio": "Ad Studio — Performance Creatives — Prosperabuzz",
  "/academy": "AI Visibility Academy — Prosperabuzz",
  "/fashion": "Fashion & Lifestyle — Prosperabuzz",
  "/for-investors": "For Investors & Funded Founders — Prosperabuzz",
  "/agency-partners": "White Label Partner for Agencies — Prosperabuzz",
  "/admin": "Admin — Prosperabuzz"
};

const PAGE_DESCRIPTIONS = {
  "/": "Prospera.buzz shapes perception for founders and brands. Strategic positioning, brand storytelling, AI visibility, authority building, and growth systems. We don't market businesses. We make them impossible to ignore.",
  "/ai-services": "AI marketing experts. We implement ChatGPT, Midjourney, Jasper, Sora, and 60+ AI tools for your business. Strategy, automation, training. Hire AI marketing agency.",
  "/creators": "Creators: monetize your followers — brand deals, courses, products. Brands: find UGC creators, micro-influencers, celebrity ambassadors. Prospera connects both sides.",
  "/for-artists": "Song promotion agency India. YouTube views via Google Ads, Instagram reel promotion, Spotify playlist pitching, brand deals for artists. Backend for Believe, Warner, Universal. Trusted by Karan Aujla's team.",
  "/talent": "Model and talent agency in Chandigarh, Delhi, Mumbai, Dubai. Female model registration. Brands hire verified models for product shoots, campaigns, fashion shows, events.",
  "/ad-studio": "Performance ad creatives that convert. 10 ads per package — video, carousel, static. Directed by Ashish Sharma. Built for Meta and Google Ads. Improve your ROAS.",
  "/academy": "AI Visibility Academy by Prospera.buzz. Learn how AI search works, how to get recommended by ChatGPT and Perplexity, GEO vs SEO, entity-based SEO, knowledge graph optimization, and the future of search.",
  "/fashion": "Fashion marketing agency for designers, luxury labels, bridal wear, ethnic wear, D2C fashion. Brand positioning, fashion photography, lookbooks, fashion films, e-commerce, SEO, ads. Worked with Payal Garg, Sab Handpainted, Audarya Couture. Chandigarh, Delhi, Mumbai, Dubai, Toronto, London.",
  "/for-investors": "Growth partner for VC portfolio companies. Post-funding marketing, go-to-market strategy, founder branding, fractional CMO, AI visibility. 29% of startups fail from marketing problems. We fix that.",
  "/careers": "Join Prospera.buzz. Open positions in AI, content, performance marketing, SEO, social media, brand strategy. Remote-first. Offices in Chandigarh, Dubai, Toronto.",
  "/booking": "Join the Prospera.buzz waitlist. We work with limited clients. If you're serious about growth, we'd like to hear from you."
};

function TitleSetter() {
  const location = useLocation();
  useEffect(() => {
    // Set title
    const base = PAGE_TITLES[location.pathname];
    if (base) { document.title = base; }
    else if (location.pathname.startsWith("/services/")) { document.title = `${location.pathname.split("/").pop().replace(/-/g, " ")} — Prosperabuzz`.replace(/\b\w/g, c => c.toUpperCase()); }
    else if (location.pathname.startsWith("/industry/")) { document.title = `${location.pathname.split("/").pop().replace(/-/g, " ")} Marketing — Prosperabuzz`.replace(/\b\w/g, c => c.toUpperCase()); }
    else if (location.pathname.startsWith("/ai-services/")) { document.title = `${location.pathname.split("/").pop().replace(/-/g, " ")} for Marketing — Prosperabuzz`.replace(/\b\w/g, c => c.toUpperCase()); }
    else { document.title = "Prosperabuzz — Perception Engineering & Growth"; }

    // Set meta description
    const desc = PAGE_DESCRIPTIONS[location.pathname];
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && desc) { metaDesc.setAttribute("content", desc); }

    // Set canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute("href", `https://prosperabuzz.com${location.pathname}`); }

    // Set OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogTitle) ogTitle.setAttribute("content", base || document.title);
    if (ogDesc && desc) ogDesc.setAttribute("content", desc);
    if (ogUrl) ogUrl.setAttribute("content", `https://prosperabuzz.com${location.pathname}`);

    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AppLayout() {
  const location = useLocation();
  const isStandalone = ["/admin"].includes(location.pathname);
  const isAppPage = ["/creators", "/talent"].includes(location.pathname);

  return (
    <>
      {!isStandalone && !isAppPage && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/industry/:slug" element={<IndustryPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/ai-services" element={<AIServicesPage />} />
        <Route path="/ai-services/:toolSlug" element={<AIToolPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/for-artists" element={<ArtistsPage />} />
        <Route path="/creators" element={<CreatorHubPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/talent" element={<ModelsPage />} />
        <Route path="/services/:slug" element={<ServicePage />} />
        <Route path="/ad-studio" element={<AdStudioPage />} />
          <Route path="/academy" element={<AcademyPage />} />
          <Route path="/fashion" element={<FashionPage />} />
          <Route path="/for-investors" element={<InvestorsPage />} />
          <Route path="/for-investors/:slug" element={<InvestorArticlePage />} />
          <Route path="/agency-partners" element={<AgencyPartnersPage />} />
      </Routes>
      {!isStandalone && !isAppPage && <SmritiChat />}
      {!isStandalone && !isAppPage && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-black text-white">
      <BrowserRouter>
        <TitleSetter />
        <ThemeEngine />
        <AppLayout />
        <Toaster theme="dark" />
      </BrowserRouter>
    </div>
  );
}

export default App;
